/**
 * FacturesRecuesPanel — Factures fournisseurs reçues via Chorus (commune destinataire).
 * Synchronise depuis Chorus, liste les factures, workflow : valider / rejeter /
 * mettre en paiement. Onglet « Factures reçues » de la brique compta.
 */
import { useEffect, useState } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { FactureRecue } from '@/lib/api';
import { RefreshCw, FileText, Check, X, CreditCard, Inbox } from 'lucide-react';

function euro(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function frDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const STATUT_META: Record<string, { label: string; variant: 'info' | 'success' | 'urgent' | 'warning' | 'neutral' }> = {
  a_valider: { label: 'À valider', variant: 'warning' },
  validee: { label: 'Validée', variant: 'success' },
  rejetee: { label: 'Rejetée', variant: 'urgent' },
  suspendue: { label: 'Suspendue', variant: 'neutral' },
  mise_en_paiement: { label: 'Mise en paiement', variant: 'info' },
};

export function FacturesRecuesPanel() {
  const [factures, setFactures] = useState<FactureRecue[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<string>('');
  const { confirm, prompt, dialogElement } = useConfirmDialog();

  async function charger() {
    setLoading(true);
    try {
      setFactures(await api.comptabilite.facturesRecues.list(filtre || undefined));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }
  useEffect(() => { void charger(); /* eslint-disable-next-line */ }, [filtre]);

  async function synchroniser() {
    setSyncing(true); setError(null); setInfo(null);
    try {
      const r = await api.comptabilite.facturesRecues.sync();
      setInfo(`${r.nouvelles} nouvelle(s) facture(s) récupérée(s)${r.source === 'mock' ? ' (mode démonstration)' : ''}.`);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Synchronisation impossible');
    } finally { setSyncing(false); }
  }

  async function valider(f: FactureRecue) {
    const ok = await confirm({
      title: 'Valider la facture', description: `Valider la facture ${f.numeroFacture ?? ''} de ${f.fournisseurNom ?? ''} (${euro(f.montantTtc)}) ? Cela confirme le service fait.`,
      confirmLabel: 'Valider',
    });
    if (!ok) return;
    try { await api.comptabilite.facturesRecues.valider(f.id); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Validation impossible'); }
  }

  async function rejeter(f: FactureRecue) {
    const motif = await prompt({
      title: 'Rejeter la facture', description: `Motif du rejet de la facture ${f.numeroFacture ?? ''} :`,
      confirmLabel: 'Rejeter', placeholder: 'ex. montant erroné, prestation non réalisée…', required: true, multiline: true,
    });
    if (!motif) return;
    try { await api.comptabilite.facturesRecues.rejeter(f.id, motif); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Rejet impossible'); }
  }

  async function mettreEnPaiement(f: FactureRecue) {
    const ok = await confirm({
      title: 'Mettre en paiement', description: `Mettre en paiement la facture ${f.numeroFacture ?? ''} (${euro(f.montantTtc)}) ?`,
      confirmLabel: 'Mettre en paiement',
    });
    if (!ok) return;
    try { await api.comptabilite.facturesRecues.mettreEnPaiement(f.id); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Mise en paiement impossible'); }
  }

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {info && <Alert variant="info">{info}</Alert>}

      <Card variant="glass" padded>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-ink">Factures fournisseurs reçues</h3>
            <p className="text-xs text-ink-muted">Factures adressées à la commune via Chorus Pro. Synchronisez pour récupérer les nouvelles.</p>
          </div>
          <Button onClick={() => void synchroniser()} disabled={syncing} variant="primary" size="sm">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Synchroniser
          </Button>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          {([['', 'Toutes'], ['a_valider', 'À valider'], ['validee', 'Validées'], ['mise_en_paiement', 'En paiement'], ['rejetee', 'Rejetées']] as const).map(([val, lib]) => (
            <button key={val} type="button" onClick={() => setFiltre(val)}
              className={`rounded-r-pill border px-3 py-1 text-xs font-medium transition ${filtre === val ? 'border-accent bg-accent/10 text-accent' : 'border-border bg-transparent text-ink-muted hover:text-ink'}`}>
              {lib}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center"><div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-t-transparent border-accent" role="status" aria-label="Chargement" /></div>
        ) : factures.length === 0 ? (
          <div className="py-10 text-center text-ink-muted">
            <Inbox className="mx-auto mb-2 h-8 w-8 opacity-40" />
            <p className="text-sm">Aucune facture reçue{filtre ? ' pour ce filtre' : ''}.</p>
            <p className="text-xs">Cliquez sur « Synchroniser » pour récupérer les factures depuis Chorus.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-ink-subtle">
                  <th className="px-2 py-2 text-left font-medium">Fournisseur</th>
                  <th className="px-2 py-2 text-left font-medium">N° facture</th>
                  <th className="px-2 py-2 text-left font-medium">Dépôt</th>
                  <th className="px-2 py-2 text-left font-medium">Échéance</th>
                  <th className="px-2 py-2 text-right font-medium">Montant TTC</th>
                  <th className="px-2 py-2 text-left font-medium">Statut</th>
                  <th className="px-2 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {factures.map((f) => {
                  const meta = STATUT_META[f.statutTraitement] ?? { label: f.statutTraitement, variant: 'neutral' as const };
                  return (
                    <tr key={f.id} className="border-b border-border/50">
                      <td className="px-2 py-2">
                        <div className="font-medium text-ink">{f.fournisseurNom ?? '—'}</div>
                        {f.fournisseurSiret && <div className="text-xs text-ink-subtle">SIRET {f.fournisseurSiret}</div>}
                      </td>
                      <td className="px-2 py-2 text-ink-muted">{f.numeroFacture ?? '—'}</td>
                      <td className="px-2 py-2 text-ink-muted">{frDate(f.dateDepot)}</td>
                      <td className="px-2 py-2 text-ink-muted">{frDate(f.dateEcheance)}</td>
                      <td className="px-2 py-2 text-right font-medium text-ink">{euro(f.montantTtc)}</td>
                      <td className="px-2 py-2">
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                        {f.motifRejet && <div className="mt-0.5 text-xs text-ink-subtle">{f.motifRejet}</div>}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" title="Télécharger le PDF"
                            onClick={() => void api.comptabilite.facturesRecues.telechargerPdf(f.id)}
                            className="rounded-r-md border border-border p-1.5 text-ink-muted hover:text-ink">
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                          {f.statutTraitement === 'a_valider' && (
                            <>
                              <button type="button" title="Valider" onClick={() => void valider(f)}
                                className="rounded-r-md border border-border p-1.5 text-emerald-600 hover:bg-emerald-50">
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button type="button" title="Rejeter" onClick={() => void rejeter(f)}
                                className="rounded-r-md border border-border p-1.5 text-red-600 hover:bg-red-50">
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          {f.statutTraitement === 'validee' && (
                            <button type="button" title="Mettre en paiement" onClick={() => void mettreEnPaiement(f)}
                              className="inline-flex items-center gap-1 rounded-r-md border border-border px-2 py-1.5 text-xs text-ink-muted hover:text-ink">
                              <CreditCard className="h-3.5 w-3.5" /> Payer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {dialogElement}
    </div>
  );
}
